import React, { useState } from 'react';
import './SpiriFeaturesContent.css';

function SpiriFeaturesContent({ 
    features = [], 
    updateFeature, 
    maxFeatures = 4, 
    featuresLabel = "Features",
    showPositionControl = false, // ✅ Position Control deaktiviert
    editData = {},
    handleInputChange
}) {
    // ✅ REVOLUTIONARY DESIGN - Ausklappbare Sektion
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Ensure we have exactly maxFeatures features
    const ensuredFeatures = [...features];
    while (ensuredFeatures.length < maxFeatures) {
        ensuredFeatures.push({
            id: `feature${ensuredFeatures.length + 1}`,
            position: ensuredFeatures.length < 2 ? 'left' : 'right',
            title: '',
            description: ''
        });
    }

    const handleToggleFeature = (index, checked) => {
        const fieldName = `showFeature${index + 1}`;
        handleInputChange({ 
            target: { 
                name: fieldName, 
                value: checked, 
                type: 'checkbox', 
                checked: checked 
            } 
        });
    };

    const isFeatureVisible = (index) => {
        const fieldName = `showFeature${index + 1}`;
        return editData[fieldName] !== false; // Default true if undefined
    };

    return (
        <div className="revolutionary-spiri-features-container">
            {/* ✅ REVOLUTIONARY HEADER - Nach TextSettingsSection Pattern */}
            <div 
                className={`spiri-features-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="spiri-features-header-content">
                    <div className="spiri-features-icon">
                        <i className="fa fa-star"></i>
                    </div>
                    <div className="spiri-features-title">{featuresLabel}</div>
                    <div className="spiri-features-count">{maxFeatures}</div>
                </div>
                <div className={`spiri-features-toggle-arrow ${isExpanded ? 'rotated' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* ✅ EXPANDED CONTENT - Nur sichtbar wenn ausgeklappt */}
            {isExpanded && (
                <div className="spiri-features-content-expanded">
                    <div className="spiri-features-grid">
                {ensuredFeatures.slice(0, maxFeatures).map((feature, index) => (
                    <div key={feature.id || index} className="spiri-feature-card">
                        
                        {/* Feature Header with Toggle */}
                        <div className="spiri-feature-header">
                            <div className="spiri-feature-number">
                                Feature {index + 1}
                            </div>
                            
                            <div className="spiri-feature-toggle-container">
                                <label className="spiri-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={isFeatureVisible(index)}
                                        onChange={(e) => handleToggleFeature(index, e.target.checked)}
                                        className="spiri-toggle-input"
                                    />
                                    <span className="spiri-toggle-slider"></span>
                                </label>
                                <span className="spiri-toggle-label">
                                    {isFeatureVisible(index) ? 'Visible' : 'Hidden'}
                                </span>
                            </div>
                        </div>


                        {/* Feature Content */}
                        <div className="spiri-feature-content">
                            {/* Mini Title Input */}
                            <div className="spiri-input-group">
                                <label className="spiri-input-label">
                                    <i className="fas fa-tag spiri-input-icon"></i>
                                    Mini Title
                                    <div className="spiri-feature-toggle-inline">
                                        <label className="spiri-toggle-switch-mini">
                                            <input
                                                type="checkbox"
                                                checked={feature.showMiniTitle !== false}
                                                onChange={(e) => updateFeature(index, 'showMiniTitle', e.target.checked)}
                                                className="spiri-toggle-input-mini"
                                            />
                                            <span className="spiri-toggle-slider-mini"></span>
                                        </label>
                                    </div>
                                </label>
                                <input
                                    type="text"
                                    value={feature.miniTitle || ''}
                                    onChange={(e) => updateFeature(index, 'miniTitle', e.target.value)}
                                    placeholder={`Mini title ${index + 1}...`}
                                    className="spiri-text-input"
                                    disabled={feature.showMiniTitle === false}
                                />
                            </div>

                            {/* Title Input */}
                            <div className="spiri-input-group">
                                <label className="spiri-input-label">
                                    <i className="fas fa-heading spiri-input-icon"></i>
                                    Title
                                </label>
                                <input
                                    type="text"
                                    value={feature.title || ''}
                                    onChange={(e) => updateFeature(index, 'title', e.target.value)}
                                    placeholder={`Feature ${index + 1} title...`}
                                    className="spiri-text-input"
                                />
                            </div>

                            {/* Description Input */}
                            <div className="spiri-input-group">
                                <label className="spiri-input-label">
                                    <i className="fas fa-align-left spiri-input-icon"></i>
                                    Description
                                </label>
                                <textarea
                                    value={feature.description || ''}
                                    onChange={(e) => updateFeature(index, 'description', e.target.value)}
                                    placeholder={`Feature ${index + 1} description...`}
                                    className="spiri-textarea-input"
                                    rows="4"
                                />
                            </div>

                            {/* Price Label Input */}
                            <div className="spiri-input-group">
                                <label className="spiri-input-label">
                                    <i className="fas fa-dollar-sign spiri-input-icon"></i>
                                    Price Label
                                    <div className="spiri-feature-toggle-inline">
                                        <label className="spiri-toggle-switch-mini">
                                            <input
                                                type="checkbox"
                                                checked={feature.showPriceLabel !== false}
                                                onChange={(e) => updateFeature(index, 'showPriceLabel', e.target.checked)}
                                                className="spiri-toggle-input-mini"
                                            />
                                            <span className="spiri-toggle-slider-mini"></span>
                                        </label>
                                    </div>
                                </label>
                                <input
                                    type="text"
                                    value={feature.priceLabel || ''}
                                    onChange={(e) => updateFeature(index, 'priceLabel', e.target.value)}
                                    placeholder={`$99/month`}
                                    className="spiri-text-input"
                                    disabled={feature.showPriceLabel === false}
                                />
                            </div>
                        </div>

                        {/* Visibility Indicator */}
                        <div className={`spiri-visibility-indicator ${isFeatureVisible(index) ? 'visible' : 'hidden'}`}>
                            <i className={`fas ${isFeatureVisible(index) ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                            {isFeatureVisible(index) ? 'Feature is visible' : 'Feature is hidden'}
                        </div>
                    </div>
                ))}
                    </div>

                    <div className="spiri-features-footer">
                        <div className="spiri-features-info">
                            <i className="fas fa-info-circle"></i>
                            Use the toggles to show/hide individual features in your spiritual banner.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SpiriFeaturesContent;
